import React from "react";
import { connect } from "react-redux";
import { TimelinePanel } from "cm-component-library/lib/components/TimelinePanel";
import { ThunkDispatch } from "redux-thunk";
import { Row, Col } from "reactstrap";
import CMTimeline from "cm-component-library/lib/components/Timeline/TimelineContainer";
import DateRangeSelectorContainer from "cm-component-library/lib/components/DateRangeSelector/DateRangeSelectorContainer";
import { TimelineEvent } from "cm-component-library/lib/components/Timeline/Models/TimelineEvent";
import dateFormat from "dateformat";
import { Link, withRouter, RouteComponentProps } from "react-router-dom";
import { timelineService } from "../..";
import { AdvancedSearchSettings } from "../../Models/AdvancedSearch/AdvancedSearchSettings";
import {
  AdvancedSearchAction,
  toggleTimelineDropdown,
  IGetFeedbackCreationEventsArguments,
  setFeedbackCreationEventsLoading,
  getFeedbackCreationEvents,
  IUpdateAdvancedSearchSettingsArguments,
  updateAdvancedSearchSettings
} from "../../Actions/AdvancedSearchActions";
import { IAppState, LoadingState, LoadingDisplay, ErrorDisplay, appPaths } from "../../../Common";
import { SearchContext } from "../../Models/SearchContext";
import styles from "../../Styles/TimelineContainer.module.scss";
import { userService } from "../../../User";

type Props = {};

type StateProps = {
  isLoggedIn: boolean;
  isDropdownShowing: boolean;
  advancedSearchSettings: AdvancedSearchSettings;
  loadedValues: any[];
  loadNumber: number;
  loadState: LoadingState;
  errorCode?: number;
  errorMessage?: string;
  selectedContext: SearchContext;
  utcOffset?: number;
};

type DispatchProps = {
  toggleTimelineDropdown: () => void;
  setFeedbackCreationEventsLoading: () => void;
  getFeedbackCreationEvents: (args: IGetFeedbackCreationEventsArguments) => void;
  updateAdvancedSearchSettings: (args: IUpdateAdvancedSearchSettingsArguments) => void;
};

type State = {
  // these are the two from the datepicker
  startTimelineGraphDate: Date;
  endTimelineGraphDate: Date;
  startSelectedRangeDate?: Date;
  endSelectedRangeDate?: Date;
  isTimelineAnimating: boolean;
  selectedColIndex?: number;
};

function mapStateToProbs(state: IAppState): StateProps {
  return {
    isLoggedIn: !!state.oidc.user,
    isDropdownShowing: state.advancedSearch.isTimelineDropdownShowing,
    advancedSearchSettings: state.advancedSearch.settings,
    loadedValues: state.advancedSearch.feedbackCreationEvents,
    loadNumber: state.advancedSearch.feedbackCreationEventsLoadCount,
    loadState: state.advancedSearch.feedbackCreationEventsLoadState,
    errorCode: state.advancedSearch.feedbackCreationEventsErrorCode,
    errorMessage: state.advancedSearch.feedbackCreationEventsErrorMessage,
    selectedContext: state.searchContext.selectedContext,
    utcOffset: state.oidc.user ? userService.getUtcOffset(state.oidc) : undefined
  };
}

function mapDispatchToProps(
  dispatch: ThunkDispatch<IAppState, {}, AdvancedSearchAction>
): DispatchProps {
  return {
    toggleTimelineDropdown: () => dispatch(toggleTimelineDropdown()),
    setFeedbackCreationEventsLoading: () => dispatch(setFeedbackCreationEventsLoading()),
    getFeedbackCreationEvents: (args: IGetFeedbackCreationEventsArguments) =>
      dispatch(getFeedbackCreationEvents(args)),
    updateAdvancedSearchSettings: (args: IUpdateAdvancedSearchSettingsArguments) =>
      dispatch(updateAdvancedSearchSettings(args))
  };
}

class TimelineContainer extends React.Component<
  Props & StateProps & DispatchProps & RouteComponentProps,
  State
> {
  constructor(props: Props & StateProps & DispatchProps & RouteComponentProps) {
    super(props);

    // change the date values for the default value since we want to have a grey gutter on either side
    const startDate = timelineService.addDays(this.props.advancedSearchSettings.startDate, -2);
    const endDate = timelineService.addDays(
      new Date(this.props.advancedSearchSettings.endDate.getTime()),
      2
    );
    // endDate.setTime(endDate.getTime() - 1000);
    endDate.setHours(23, 59, 0, 0);

    this.state = {
      startTimelineGraphDate: startDate,
      endTimelineGraphDate: endDate,
      isTimelineAnimating: false
    };

    this.onTimelineAnimationStart = this.onTimelineAnimationStart.bind(this);
    this.onTimelineAnimationComplete = this.onTimelineAnimationComplete.bind(this);
    this.onToggle = this.onToggle.bind(this);
    this.onGraphDatesChange = this.onGraphDatesChange.bind(this);
    this.onSelectedRangeChange = this.onSelectedRangeChange.bind(this);
    this.onClickRange = this.onClickRange.bind(this);
    this.onUpdateRangeUsingDragger = this.onUpdateRangeUsingDragger.bind(this);
  }

  // gets dummy values for the timeline
  /* getDummyTimelineValuesRandom(count: number, startDate: Date, endDate: Date ) : TimelineEvent[]{

        // get diff in days
        var diff = Math.abs(startDate.getTime() - endDate.getTime());
        var diffDays = Math.ceil(diff / (1000 * 3600 * 24)); 

        let toReturn = [];
        for(let i = 0; i < count; i++){

            let dateWithAddedDays = timelineService.addDays(startDate, Math.random() * diffDays);
            let dateWithAddedHours = timelineService.addHours(dateWithAddedDays, Math.random() * 24);
            
            toReturn.push({
                Id: `${i}`,
                Date: dateWithAddedHours
            });
        }
        return toReturn;
    } */

  componentDidUpdate(prevProps: Props & StateProps & DispatchProps) {
    if (this.props.isLoggedIn && this.props.isDropdownShowing) {
      // check to see if nothing is loaded or loading
      if (
        !this.props.loadState.isLoaded &&
        !this.props.loadState.isLoading &&
        !this.props.loadState.errored
      ) {
        // we need to load
        this.loadData();
      }
      // check to see if context changed
      else if (
        prevProps.selectedContext.uniqueId !== this.props.selectedContext.uniqueId &&
        !this.props.loadState.errored &&
        !this.props.loadState.isLoading
      ) {
        // context has changed, reload
        this.loadData();
      }
    }
  }

  onTimelineAnimationStart() {
    this.setState(prevState => {
      return {
        ...prevState,
        isTimelineAnimating: true
      };
    });
  }

  onTimelineAnimationComplete() {
    this.setState(prevState => {
      return {
        ...prevState,
        isTimelineAnimating: false
      };
    });
  }

  onToggle() {
    this.props.toggleTimelineDropdown();
  }

  // This is a change of the lower selector- that determines the width of the timeline
  onGraphDatesChange(start: Date, end: Date) {
    // convert end of range to end of day
    end.setHours(23, 59, 0, 0);

    this.setState(
      prevState => ({
        ...prevState,
        startTimelineGraphDate: start,
        endTimelineGraphDate: end,
        selectedColIndex: undefined
      }),
      () => {
        this.loadData();
      }
    );
  }

  onSelectedRangeChange(start: Date, end: Date) {
    // only update if the values have changed
    if (
      start.getTime() !== this.props.advancedSearchSettings.startDate.getTime() ||
      end.getTime() !== this.props.advancedSearchSettings.endDate.getTime()
    ) {
      // update selected range
      this.props.updateAdvancedSearchSettings({
        settings: {
          ...this.props.advancedSearchSettings,
          startDate: start,
          endDate: end
        }
      });
    }
  }

  onUpdateRangeUsingDragger(start: Date, end: Date, triggeredByUser: boolean) {
    // Update selected col (unselect whatever is selected)
    this.setState(
      (prevState: State) => ({
        ...prevState,
        selectedColIndex: undefined
      }),
      () => {
        if (triggeredByUser) {
          // navigate to advanced search if we need to
          if (this.props.location.pathname !== appPaths.search.advanced) {
            this.props.history.push(appPaths.search.advanced);
          }
        }

        this.onSelectedRangeChange(start, end);
      }
    );
  }

  onClickRange(start: Date, end: Date, colIndex: number) {
    // update selected col
    this.setState(
      (prevState: State) => ({
        ...prevState,
        selectedColIndex: colIndex
      }),
      () => {
        // navigate to advanced search if we need to
        if (this.props.location.pathname !== appPaths.search.advanced) {
          this.props.history.push(appPaths.search.advanced);
        }

        this.onSelectedRangeChange(start, end);
      }
    );
  }

  getTitleForRange(start: Date, end: Date) {
    // let startString = dateFormat(start, "mmmm dS h:MM TT");
    // let endString = dateFormat(end, "mmmm dS h:MM TT");

    return "Click to view these Feedbacks";

    // return `Click to view Feedback created from ${startString} to ${endString}`;
  }

  loadData() {
    this.props.setFeedbackCreationEventsLoading();
    this.props.getFeedbackCreationEvents({
      start: this.state.startTimelineGraphDate,
      end: this.state.endTimelineGraphDate,
      context: this.props.selectedContext,
      utcOffset: this.props.utcOffset as number
    });
  }

  renderPanelInterior(): JSX.Element {
    if (this.props.loadState.errored) {
      return (
        <ErrorDisplay
          errorCode={this.props.errorCode}
          errorDescription={this.props.errorMessage}
          errorResult="Could not get Timeline data"
        />
      );
    }
    return (
      <div className={styles.timelineContainer}>
        <div className={styles.timeline}>
          <CMTimeline
            events={this.props.loadedValues}
            eventsLoadNumber={this.props.loadNumber}
            startGraphDate={this.state.startTimelineGraphDate}
            endGraphDate={this.state.endTimelineGraphDate}
            isVisible={this.props.isDropdownShowing}
            onAnimationStart={this.onTimelineAnimationStart}
            onAnimationComplete={this.onTimelineAnimationComplete}
            onRangeChangeComplete={this.onUpdateRangeUsingDragger}
            onRangeClick={this.onClickRange}
            getTitleForRange={this.getTitleForRange}
            selectedColIndex={this.state.selectedColIndex}
          />
          <Row>
            <Col md={8}>
              <DateRangeSelectorContainer
                startDate={this.state.startTimelineGraphDate}
                endDate={this.state.endTimelineGraphDate}
                latestDate={timelineService.addDays(new Date(), 2)}
                startDateId="start-date-1"
                endDateId="end-date-1"
                onDatesChange={this.onGraphDatesChange}
              />
            </Col>
            <Col md={4} style={{ textAlign: "right" }}>
              {this.props.location.pathname !== appPaths.search.advanced && (
                <Link className="btn btn-secondary" to={appPaths.search.advanced}>
                  View Results
                </Link>
              )}
            </Col>
          </Row>
        </div>
      </div>
    );
  }

  render() {
    return (
      <TimelinePanel isOpen={this.props.isDropdownShowing} onToggle={this.onToggle}>
        {this.renderPanelInterior()}
      </TimelinePanel>
    );
  }
}

export default connect(mapStateToProbs, mapDispatchToProps)(withRouter(TimelineContainer));
